import { ButtonComponent } from '@/components/commons'
import { cssClass, isUrlValid } from '@/shared/utils'
import { Dropzone } from '@mantine/dropzone'
import { EditIconPen, FolderIcon } from '@/public/assets/v2/images/icons'
import { InputComponent } from '@/components/commons/forms/input/Input.component'
import { skeleton } from '@/components/skeleton/styles/index.css'
import { Title } from '@/components/commons/typography/Title'
import { useEffect, useRef, useState } from 'react'
import { useHighlightEditorStore } from 'containers/highlights/store/highlightEditor'
import { useImageFormUrl } from 'hooks/images/useImageFormUrl'
import { validateUrl } from 'utilities/validations/urls'
import Image from 'next/image'
import { Text } from '@/components/commons/typography/Text'
import { HSocialNetwork } from './HSocialNetwork'
import { getSocialNetworkByUrl } from '@/shared/utils/project'
import { useHighlightError } from 'containers/highlights/hooks/useHighlights'
import { useHighlightImageUploader } from 'containers/highlights/hooks/useHighlightImage'

export const HContentLink = () => {
  const { highlight, updateHighlight, setUploadImageRequired } =
    useHighlightEditorStore()
  const { refetch, isFetching: fetchingMetaData } = useImageFormUrl(
    highlight?.contentLink
  )
  const { errorMessage } = useHighlightError('contentLink')
  const { errorMessage: imageErrorMessage } = useHighlightError('image')
  const {
    mutateAsync,
    isLoading,
    data: imageUploaded,
  } = useHighlightImageUploader()
  const [showValidUrlError, setShowValidUrlError] = useState<boolean>(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const openRef = useRef<() => void>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (imageUploaded) {
      updateHighlight({ image: imageUploaded })
      setUploadImageRequired(false)
    }
  }, [imageUploaded, setUploadImageRequired, updateHighlight])

  const uploadImageFromUrl = async (imageBlob: Blob) => {
    const file = new File([imageBlob], 'image.png', { type: 'image/png' })
    mutateAsync({ file, id: highlight?.id })
  }

  const onBlur = async () => {
    const url = highlight?.contentLink

    if (!url) return setShowValidUrlError(false)

    if (validateUrl(url)) {
      const social = getSocialNetworkByUrl(url)

      updateHighlight({ contentPlatform: social || 'web' })

      setShowValidUrlError(false)

      // if (!highlight?.image) refetch()
      if (validateUrl(url) && isUrlValid(url)) {
        const { data } = await refetch()
  
        if (data) {
          uploadImageFromUrl(data)
        }
      }
    } else {
      setShowValidUrlError(true)
    }
  }

  const onDropImage = async (files: File[]) => {
    const file = files[0]
    await mutateAsync({ file, id: highlight?.id })
  }

  const onErrorImage = () => {
    updateHighlight({ image: undefined })
  }

  return (
    <>
      <div className="flex flex-column gap-8">
        <Title as="label" variation="xSmall">
          Content Link
          <Text as="sup" colorClass="text-info">
            *
          </Text>
        </Title>
        <InputComponent
          size="small"
          placeholder="Post your link"
          usePlaceholder
          className={['bg-white-primary']}
          value={highlight?.contentLink}
          onChange={(e) =>
            updateHighlight({ contentLink: e.currentTarget.value })
          }
          error={
            (errorMessage && errorMessage) ||
            (showValidUrlError && 'Please enter a valid url')
          }
          onBlur={onBlur}
        />
      </div>
      <HSocialNetwork />
      <div className="flex flex-column gap-8">
        <Title as="label" variation="xSmall">
          Thumbnail Selection
          <Text as="sup" colorClass="text-info">
            *
          </Text>
        </Title>
        {highlight?.image && (
          <div
            className={cssClass([
              'relative full-w',
              imageLoaded ? 'bg-darkGray2' : skeleton,
            ])}
            style={{ aspectRatio: '328/335' }}
          >
            <Image
              src={highlight.image}
              alt={highlight?.title + '_thumbnail_editor' + highlight?.image}
              fill
              style={{ objectFit: 'contain' }}
              onLoad={() => setImageLoaded(true)}
              ref={imageRef}
              onError={onErrorImage}
            />
            <div className="absolute top-20 right-20">
              <ButtonComponent
                iconLeft={<EditIconPen />}
                label="Edit"
                variation="white-bg"
                onClick={() => openRef.current?.()}
                size="xSmall"
              />
            </div>
          </div>
        )}
        <Dropzone
          openRef={openRef}
          onDrop={onDropImage}
          className="--v2 cursor-pointer"
          maxFiles={1}
          disabled={isLoading || fetchingMetaData}
          loading={isLoading || fetchingMetaData}
        >
          {!highlight?.image && (
            <div className="full-w flex items-center flex-column justify-center bg-tertiary rounded-16 gap-8 py-32 border-dashed border-secondary">
              <FolderIcon className="text-tertirary text-22" />
              <Title as="label" variation="xSmall">
                Drop files here or click to upload
              </Title>
              <Text variation="small" colorClass="text-tertiary">
                Support PNG, JPG
              </Text>
            </div>
          )}
        </Dropzone>
        {imageErrorMessage && (
          <Text variation="xSmall" colorClass="text-danger">
            {imageErrorMessage}
          </Text>
        )}
      </div>
    </>
  )
}
